import React, { useState, useEffect } from 'react';
import { 
    FormControl, 
    InputLabel,
    Select,
    MenuItem,
    Box, 
    TextField, 
    Button, 
    IconButton,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const DropDownMenuComponent = ({ properties, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const { options = {}, constrains = {} } = properties;

    // Get dropdown options from properties or saved value
    const initialOptions = value?.options || options.options || ['Option 1', 'Option 2', 'Option 3'];
    const initialSelectedValue = value?.selectedValue || options.defaultValue || '';
    const initialMinOptions = value?.minOptions || constrains?.minOptions || 1;
    const initialMaxOptions = value?.maxOptions || constrains?.maxOptions || 20;
    
    const [dropdownOptions, setDropdownOptions] = useState(initialOptions);
    const [selectedValue, setSelectedValue] = useState(initialSelectedValue);
    const [newOptionText, setNewOptionText] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [minOptions, setMinOptions] = useState(initialMinOptions);
    const [maxOptions, setMaxOptions] = useState(initialMaxOptions);
    const [showConstraints, setShowConstraints] = useState(false);

    // Sync with parent value changes (when parent updates the value)
    useEffect(() => {
        if (value && typeof value === 'object') {
            if (value.options && Array.isArray(value.options)) {
                setDropdownOptions(value.options);
            }
            if (value.selectedValue !== undefined) {
                setSelectedValue(value.selectedValue);
            }
            if (value.minOptions !== undefined) {
                setMinOptions(value.minOptions);
            }
            if (value.maxOptions !== undefined) {
                setMaxOptions(value.maxOptions);
            }
        }
    }, [value]);

    // Sync dropdown options with parent when they change
    useEffect(() => {
        if (valueUpdaterCallback) {
            // Store the selected value, available options, and constraints
            valueUpdaterCallback({
                selectedValue,
                options: dropdownOptions,
                minOptions,
                maxOptions
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOptions, selectedValue, minOptions, maxOptions]);

    const handleDropdownChange = (event) => {
        const value = event.target.value;
        setSelectedValue(value);

        // Validity check - if required, value must not be empty
        const isValid = options.required ? value !== '' : true;
        if (validityUpdaterCallback) {
            validityUpdaterCallback(isValid);
        }

        // Call custom onChange if provided
        if (options.onChange) {
            options.onChange(value);
        }
    };

    const handleAddOption = () => {
        if (newOptionText.trim() === '') {
            return;
        }

        // Check max options constraint
        if (dropdownOptions.length >= maxOptions) {
            alert(`Maximum ${maxOptions} options allowed`);
            return;
        }

        setDropdownOptions([...dropdownOptions, newOptionText.trim()]);
        setNewOptionText('');
    };

    const handleDeleteOption = (index) => {
        // Check min options constraint
        if (dropdownOptions.length <= minOptions) {
            alert(`Minimum ${minOptions} options required`);
            return;
        }

        const deletedOption = dropdownOptions[index];
        const newOptions = dropdownOptions.filter((_, i) => i !== index);
        setDropdownOptions(newOptions);

        // Clear selected value if it was the deleted option
        if (selectedValue === deletedOption) {
            setSelectedValue('');
        }
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditingText(dropdownOptions[index]);
    };

    const handleSaveEdit = () => {
        if (editingText.trim() === '') {
            return;
        }

        const newOptions = [...dropdownOptions];
        const oldValue = newOptions[editingIndex];
        newOptions[editingIndex] = editingText.trim();
        setDropdownOptions(newOptions);

        // Update selected value if it was the edited option
        if (selectedValue === oldValue) {
            setSelectedValue(editingText.trim());
        }

        setEditingIndex(null);
        setEditingText('');
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditingText('');
    };

    const handleMinOptionsChange = (event) => {
        const newMin = parseInt(event.target.value, 10);
        if (isNaN(newMin) || newMin < 1) {
            return;
        }
        
        // Ensure min is not greater than max
        if (newMin > maxOptions) {
            alert('Minimum options cannot be greater than maximum options');
            return;
        }

        // Ensure current options count meets the new minimum
        if (dropdownOptions.length < newMin) {
            alert(`You need at least ${newMin} options. Current: ${dropdownOptions.length}`);
            return;
        }

        setMinOptions(newMin);
    };

    const handleMaxOptionsChange = (event) => {
        const newMax = parseInt(event.target.value, 10);
        if (isNaN(newMax) || newMax < 1) {
            return;
        }

        // Ensure max is not less than min
        if (newMax < minOptions) {
            alert('Maximum options cannot be less than minimum options');
            return;
        }

        // Ensure current options count doesn't exceed the new maximum
        if (dropdownOptions.length > newMax) {
            alert(`You have ${dropdownOptions.length} options. Please reduce to ${newMax} or less before changing maximum.`);
            return;
        }

        setMaxOptions(newMax);
    };

    const toggleConstraints = () => {
        setShowConstraints(!showConstraints);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
            {/* Dropdown Selection Area */}
            <FormControl fullWidth required={options.required || false} sx={{ width: '100%' }}>
                <InputLabel id={`dropdown-label-${properties.label}`}>
                    {options.label || 'Dropdown Menu'}
                </InputLabel>
                <Select
                    labelId={`dropdown-label-${properties.label}`}
                    value={selectedValue}
                    onChange={handleDropdownChange}
                    label={options.label || 'Dropdown Menu'}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {dropdownOptions.map((option, index) => (
                        <MenuItem key={index} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Options Management Area */}
            <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Manage Options
                </Typography>
                <List dense>
                    {dropdownOptions.map((option, index) => (
                        <ListItem 
                            key={index}
                            sx={{ 
                                backgroundColor: '#fff', 
                                mb: 1, 
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            {editingIndex === index ? (
                                <Box display="flex" alignItems="center" width="100%" gap={1}>
                                    <TextField
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSaveEdit();
                                            }
                                        }}
                                    />
                                    <IconButton 
                                        size="small" 
                                        color="primary" 
                                        onClick={handleSaveEdit}
                                    >
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleCancelEdit}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <>
                                    <ListItemText primary={option} />
                                    <IconButton 
                                        size="small" 
                                        color="primary" 
                                        onClick={() => handleStartEdit(index)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        color="error" 
                                        onClick={() => handleDeleteOption(index)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {/* Add New Option Area */}
            <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Add New Option
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                        size="small"
                        placeholder="Enter option text"
                        value={newOptionText}
                        onChange={(e) => setNewOptionText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleAddOption();
                            }
                        }}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddOption}
                        disabled={newOptionText.trim() === ''}
                    >
                        Add
                    </Button>
                </Box>
            </Paper>

            {/* Constraints Configuration Area */}
            <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: '#e3f2fd' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={showConstraints ? 2 : 0}>
                    <Typography variant="subtitle2">
                        Constraints
                    </Typography>
                    <Button
                        size="small"
                        onClick={toggleConstraints}
                        variant="outlined"
                    >
                        {showConstraints ? 'Hide' : 'Show'}
                    </Button>
                </Box>
                
                {showConstraints && (
                    <Box display="flex" gap={2} flexDirection="column">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body2" sx={{ minWidth: '120px' }}>
                                Min Options:
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={minOptions}
                                onChange={handleMinOptionsChange}
                                inputProps={{ min: 1, max: maxOptions }}
                                sx={{ width: '100px' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                (Current: {dropdownOptions.length})
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body2" sx={{ minWidth: '120px' }}>
                                Max Options:
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={maxOptions}
                                onChange={handleMaxOptionsChange}
                                inputProps={{ min: minOptions, max: 100 }}
                                sx={{ width: '100px' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                (Current: {dropdownOptions.length})
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DropDownMenuComponent;
